import type { Entry } from '../entry';

/* Account settings, team members, invitations and onboarding. */
export const accountTeamMessages = {
  accountUnknownError: { en: 'Unknown error', ja: '不明なエラー' },
  accountConsoleSessionUnavailable: {
    en: 'Console session is unavailable',
    ja: 'コンソールのセッションを利用できません',
  },
  accountProfileUpdated: { en: 'Profile updated.', ja: 'プロフィールを更新しました。' },
  accountBackupEmailAdded: { en: 'Backup email added.', ja: 'バックアップメールを追加しました。' },
  accountBackupEmailRemoved: {
    en: 'Backup email removed.',
    ja: 'バックアップメールを削除しました。',
  },
  accountOrganizationUpdated: { en: 'Updated {name}.', ja: '{name}を更新しました。' },
  accountLeaveOrganizationConfirm: {
    en: 'Leave {name}? Owners can leave only when another owner remains.',
    ja: '{name}から退出しますか？オーナーが退出できるのは、ほかのオーナーが残っている場合のみです。',
  },
  accountDeleteOrganizationConfirm: {
    en: 'Delete {name}? This permanently removes the organization and its projects if no other members, wallets, or retained history exist. Deleting your current organization signs you out.',
    ja: '{name}を削除しますか？ほかのメンバー、ウォレット、保持される履歴がない場合、組織とそのプロジェクトは完全に削除されます。現在の組織を削除すると、サインアウトされます。',
  },
  accountOrganizationDeleted: { en: 'Deleted {name}.', ja: '{name}を削除しました。' },
  accountSettingsLoadingAria: {
    en: 'Account settings loading state',
    ja: 'アカウント設定の読み込み状態',
  },
  accountSettingsLoadingLabel: {
    en: 'Loading account settings',
    ja: 'アカウント設定を読み込んでいます',
  },
  accountSettingsErrorAria: {
    en: 'Account settings error state',
    ja: 'アカウント設定のエラー状態',
  },
  accountRenameModalAria: { en: 'Rename organization modal', ja: '組織名の変更モーダル' },
  accountRenameOrganizationTitle: { en: 'Rename organization', ja: '組織名の変更' },
  accountOrganizationName: { en: 'Organization name', ja: '組織名' },
  accountCancel: { en: 'Cancel', ja: 'キャンセル' },
  accountSaving: { en: 'Saving...', ja: '保存しています…' },
  accountRename: { en: 'Rename', ja: '名前を変更' },
  accountEditProfileModalAria: { en: 'Edit profile modal', ja: 'プロフィール編集モーダル' },
  accountEditProfileTitle: { en: 'Edit profile', ja: 'プロフィールを編集' },
  accountDisplayName: { en: 'Display name', ja: '表示名' },
  accountPrimaryEmailReadOnly: {
    en: 'Primary email (read-only)',
    ja: 'プライマリメール（読み取り専用）',
  },
  accountPrimaryEmail: { en: 'Primary email', ja: 'プライマリメール' },
  accountPrimaryEmailManaged: {
    en: 'Primary email is managed by your identity provider.',
    ja: 'プライマリメールは IDプロバイダーが管理しています。',
  },
  accountBackupEmailsHeading: { en: 'Backup Emails', ja: 'バックアップメール' },
  accountBackupEmailMeta: { en: '{status} • added {date}', ja: '{status} • 追加日 {date}' },
  accountRemoving: { en: 'Removing...', ja: '削除しています…' },
  accountRemove: { en: 'Remove', ja: '削除' },
  accountBackupEmail: { en: 'Backup email', ja: 'バックアップメール' },
  accountAdding: { en: 'Adding...', ja: '追加しています…' },
  accountAdd: { en: 'Add', ja: '追加' },
  accountSave: { en: 'Save', ja: '保存' },
  accountSettingsPageAria: { en: 'Account settings page', ja: 'アカウント設定ページ' },
  accountProfile: { en: 'Profile', ja: 'プロフィール' },
  accountEdit: { en: 'Edit', ja: '編集' },
  accountNotSet: { en: 'Not set', ja: '未設定' },
  accountSignInMethod: { en: 'Sign-in method', ja: 'サインイン方法' },
  accountBackupEmailsLabel: { en: 'Backup emails', ja: 'バックアップメール' },
  accountBackupEmailsConfigured: { en: '{count} configured', ja: '{count}件設定済み' },
  accountNoneConfigured: { en: 'None configured', ja: '未設定' },
  accountManageBackupEmails: {
    en: 'Manage backup emails via Edit.',
    ja: 'バックアップメールは「編集」から管理できます。',
  },
  accountMyOrganizations: { en: 'My Organizations', ja: '自分の組織' },
  accountOrganizationsHelp: {
    en: 'Create organizations, rename the ones you manage, or leave the current organization. Manage owners and administrators from Team.',
    ja: '組織の作成、管理している組織の名前変更、現在の組織からの退出ができます。オーナーと管理者は「チーム」から管理します。',
  },
  accountCreateOrganization: { en: 'Create an organization', ja: '組織を作成' },
  accountOrganizationsAria: { en: 'Organizations', ja: '組織' },
  accountColOrganization: { en: 'Organization', ja: '組織' },
  accountColUpdated: { en: 'Updated', ja: '更新日時' },
  accountColScope: { en: 'Scope', ja: 'スコープ' },
  accountColStatus: { en: 'Status', ja: 'ステータス' },
  accountColActions: { en: 'Actions', ja: '操作' },
  accountRenameMenu: { en: 'Rename…', ja: '名前を変更…' },
  accountDeleting: { en: 'Deleting…', ja: '削除しています…' },
  accountDelete: { en: 'Delete', ja: '削除' },
  accountLeaving: { en: 'Leaving…', ja: '退出しています…' },
  accountLeaveOrganization: { en: 'Leave organization', ja: '組織から退出' },
  accountLastOwnerMustRemain: {
    en: 'At least one other owner must remain.',
    ja: 'ほかのオーナーが少なくとも1人残る必要があります。',
  },
  accountBadgeNeedsOnboarding: { en: 'Needs onboarding', ja: 'オンボーディングが必要' },
  accountBadgeCurrent: { en: 'Current', ja: '現在' },
  accountBadgeReady: { en: 'Ready', ja: '準備完了' },
  accountOpening: { en: 'Opening...', ja: '開いています…' },
  accountOpen: { en: 'Open', ja: '開く' },
  accountMoreActionsFor: { en: 'More actions for {name}', ja: '{name}のその他の操作' },
  accountNoOrganizations: {
    en: 'No organizations created by this account yet.',
    ja: 'このアカウントで作成された組織はまだありません。',
  },
  accountServiceStatusActive: { en: 'Active', ja: '有効' },
  accountServiceStatusProvisioning: { en: 'Provisioning', ja: 'プロビジョニング中' },
  accountServiceStatusDisabled: { en: 'Disabled', ja: '無効' },
  accountServiceStatusFailed: { en: 'Failed', ja: '失敗' },
  accountServiceProjectsTitle: { en: '{label} projects', ja: '{label}のプロジェクト' },
  accountServiceProjectsHelp: {
    en: 'Projects in the current organization that run {label}. Create projects and enable services in ',
    ja: '現在の組織で{label}を実行しているプロジェクトです。プロジェクトの作成とサービスの有効化は',
  },
  accountServiceProjectsHelpEnd: { en: '.', ja: 'で行います。' },
  accountColProject: { en: 'Project', ja: 'プロジェクト' },
  accountColCreated: { en: 'Created', ja: '作成日時' },
  accountServiceProjectsLoading: {
    en: 'Loading {label} projects…',
    ja: '{label}のプロジェクトを読み込んでいます…',
  },
  accountServiceProjectsUnavailable: {
    en: '{label} projects are unavailable: {message}',
    ja: '{label}のプロジェクトを利用できません: {message}',
  },
  accountServiceProjectsEmpty: {
    en: 'No project in this organization runs {label}.',
    ja: 'この組織に{label}を実行しているプロジェクトはありません。',
  },
  invitationEmailMismatch: {
    en: 'This invitation belongs to another email address. Use the account that received it.',
    ja: 'この招待は別のメールアドレス宛てです。招待を受け取ったアカウントを使用してください。',
  },
  invitationInvalidResponse: {
    en: 'The invitation response was invalid. Try again.',
    ja: '招待のレスポンスが無効でした。もう一度お試しください。',
  },
  invitationAcceptanceFailed: {
    en: 'Invitation acceptance failed.',
    ja: '招待の承諾に失敗しました。',
  },
  invitationAcceptanceFailedRetry: {
    en: 'Invitation acceptance failed. Try again.',
    ja: '招待の承諾に失敗しました。もう一度お試しください。',
  },
  invitationLinkIncomplete: {
    en: 'This invitation link is incomplete. Open the link from your email.',
    ja: 'この招待リンクは不完全です。メールに記載されたリンクを開いてください。',
  },
  invitationAllowSessionStorage: {
    en: 'Allow session storage in this browser, then open the invitation link again.',
    ja: 'このブラウザーでセッションストレージを許可してから、招待リンクをもう一度開いてください。',
  },
  invitationCheckSignInFailed: {
    en: 'Unable to check your sign-in. Try again.',
    ja: 'サインイン状態を確認できません。もう一度お試しください。',
  },
  invitationSignOutFailed: {
    en: 'Unable to sign out. Try again.',
    ja: 'サインアウトできません。もう一度お試しください。',
  },
  invitationCheckingSignIn: {
    en: 'Checking your sign-in...',
    ja: 'サインイン状態を確認しています…',
  },
  invitationSignInToAccept: { en: 'Sign in to accept', ja: 'サインインして承諾' },
  invitationAccepting: { en: 'Accepting invitation...', ja: '招待を承諾しています…' },
  invitationAccept: { en: 'Accept invitation', ja: '招待を承諾' },
  invitationUseAnotherAccount: { en: 'Use another account', ja: '別のアカウントを使用' },
  invitationPageAria: { en: 'Invitation acceptance page', ja: '招待の承諾ページ' },
  invitationSeamsHomeAria: { en: 'Seams home', ja: 'Seamsホーム' },
  invitationJoinTeam: { en: 'Join your team', ja: 'チームに参加' },
  invitationSignInWithAccount: {
    en: 'Sign in with the Google or GitHub account that received this invitation.',
    ja: 'この招待を受け取ったGoogleまたはGitHubアカウントでサインインしてください。',
  },
  invitationGoToConsole: { en: 'Go to console', ja: 'コンソールへ移動' },
  teamPermManageMembers: { en: 'Manage members', ja: 'メンバーの管理' },
  teamPermManageProjects: { en: 'Manage projects', ja: 'プロジェクトの管理' },
  teamPermViewBilling: { en: 'View billing', ja: '請求の閲覧' },
  teamPermManageBilling: { en: 'Manage billing', ja: '請求の管理' },
  teamNoProjectAccess: { en: 'No project access', ja: 'プロジェクトへのアクセスなし' },
  teamProjectCountOne: { en: '1 project', ja: '1件のプロジェクト' },
  teamProjectCountMany: { en: '{count} projects', ja: '{count}件のプロジェクト' },
  teamNoScopedProjectAccess: {
    en: 'No {label} project access',
    ja: '{label}プロジェクトへのアクセスなし',
  },
  teamScopedProjectCountOne: { en: '1 {label} project', ja: '{label}プロジェクト1件' },
  teamScopedProjectCountMany: {
    en: '{count} {label} projects',
    ja: '{label}プロジェクト{count}件',
  },
  teamFullOrganizationAccess: { en: 'Full organization access', ja: '組織への完全なアクセス' },
  teamReadOnlyAdministrator: { en: 'Read-only administrator', ja: '読み取り専用の管理者' },
  teamOrganizationRole: { en: 'Organization role', ja: '組織のロール' },
  teamRoleMember: { en: 'Member', ja: 'メンバー' },
  teamRoleAdministrator: { en: 'Administrator', ja: '管理者' },
  teamRoleOwner: { en: 'Owner', ja: 'オーナー' },
  teamOwnersFullAccess: {
    en: 'Owners have full organization, project, member, and billing access.',
    ja: 'オーナーは、組織、プロジェクト、メンバー、請求のすべてにアクセスできます。',
  },
  teamAdministratorPermissions: { en: 'Administrator permissions', ja: '管理者の権限' },
  teamProjectAccess: { en: 'Project access', ja: 'プロジェクトへのアクセス' },
  teamNoActiveProjects: {
    en: 'No active projects are available.',
    ja: '利用できる有効なプロジェクトはありません。',
  },
  teamAccessNone: { en: 'No access', ja: 'アクセスなし' },
  teamAccessViewer: { en: 'Viewer', ja: '閲覧者' },
  teamAccessEditor: { en: 'Editor', ja: '編集者' },
  teamMembershipItem: { en: 'membership', ja: 'メンバーシップ' },
  teamMembershipItemPlural: { en: 'memberships', ja: 'メンバーシップ' },
  teamInvitationSentTo: { en: 'Invitation sent to {email}.', ja: '{email}に招待を送信しました。' },
  teamMembershipUpdated: { en: 'Updated {email}.', ja: '{email}を更新しました。' },
  teamRemoveMemberConfirm: {
    en: 'Remove {email} from this organization?',
    ja: '{email}をこの組織から削除しますか？',
  },
  teamMemberSuspended: { en: '{email} was suspended.', ja: '{email}を一時停止しました。' },
  teamMemberReactivated: { en: '{email} was reactivated.', ja: '{email}を再有効化しました。' },
  teamMemberRemoved: { en: '{email} was removed.', ja: '{email}を削除しました。' },
  teamInvitationResent: {
    en: 'Invitation resent to {email}.',
    ja: '{email}に招待を再送信しました。',
  },
  teamInvitationRevoked: {
    en: 'Invitation to {email} was revoked.',
    ja: '{email}への招待を取り消しました。',
  },
  teamInviteModalAria: { en: 'Invite organization member', ja: '組織メンバーを招待' },
  teamInviteMember: { en: 'Invite member', ja: 'メンバーを招待' },
  teamVerifiedEmail: { en: 'Verified email', ja: '確認済みメール' },
  teamCancel: { en: 'Cancel', ja: 'キャンセル' },
  teamSending: { en: 'Sending…', ja: '送信しています…' },
  teamSendInvitation: { en: 'Send invitation', ja: '招待を送信' },
  teamEditModalAria: { en: 'Edit organization membership', ja: '組織のメンバーシップを編集' },
  teamEditMembership: { en: 'Edit membership', ja: 'メンバーシップを編集' },
  teamUseLeaveOrganization: {
    en: 'Use “Leave organization” in account settings to change your own membership.',
    ja: '自分のメンバーシップを変更するには、アカウント設定の「組織から退出」を使用してください。',
  },
  teamSaving: { en: 'Saving…', ja: '保存しています…' },
  teamSave: { en: 'Save', ja: '保存' },
  teamPageAria: { en: 'Organization team management page', ja: '組織のチーム管理ページ' },
  teamAddSecondOwner: {
    en: 'Add a second owner to protect organization access if the current owner becomes unavailable.',
    ja: '現在のオーナーが不在になった場合に備えて、組織へのアクセスを守るため2人目のオーナーを追加してください。',
  },
  teamScopeNote: {
    en: 'Members with access to {label} projects. Owners and administrators reach every project. Roles and memberships are organization-wide.',
    ja: '{label}プロジェクトにアクセスできるメンバーです。オーナーと管理者はすべてのプロジェクトにアクセスできます。ロールとメンバーシップは組織全体に適用されます。',
  },
  teamHideOutsideScope: {
    en: 'Hide people without {label} access',
    ja: '{label}にアクセスできない人を非表示',
  },
  teamShowOutsideScope: {
    en: 'Show {count} more without {label} access',
    ja: '{label}にアクセスできないほか{count}人を表示',
  },
  teamAdminRequiresAccess: {
    en: 'Team administration requires owner access or an administrator permission.',
    ja: 'チームの管理には、オーナーのアクセス権または管理者の権限が必要です。',
  },
  teamSearchMembershipsAria: { en: 'Search memberships', ja: 'メンバーシップを検索' },
  teamSearchMembers: { en: 'Search members', ja: 'メンバーを検索' },
  teamMembershipsAria: { en: 'Organization memberships', ja: '組織のメンバーシップ' },
  teamColMember: { en: 'Member', ja: 'メンバー' },
  teamColAccess: { en: 'Access', ja: 'アクセス' },
  teamColStatus: { en: 'Status', ja: 'ステータス' },
  teamColActions: { en: 'Actions', ja: '操作' },
  teamLoadingMemberships: { en: 'Loading memberships', ja: 'メンバーシップを読み込んでいます' },
  teamNoMembershipsMatched: {
    en: 'No memberships matched.',
    ja: '一致するメンバーシップはありません。',
  },
  teamEdit: { en: 'Edit', ja: '編集' },
  teamMoreActionsFor: { en: 'More actions for {email}', ja: '{email}のその他の操作' },
  teamReactivate: { en: 'Reactivate', ja: '再有効化' },
  teamSuspend: { en: 'Suspend', ja: '一時停止' },
  teamRemove: { en: 'Remove', ja: '削除' },
  teamPendingInvitations: { en: 'Pending invitations', ja: '保留中の招待' },
  teamInvitationsExpire: {
    en: 'Invitations expire after seven days and create membership only after acceptance.',
    ja: '招待は7日後に期限切れとなり、承諾後にのみメンバーシップが作成されます。',
  },
  teamPendingInvitationsAria: {
    en: 'Pending organization invitations',
    ja: '保留中の組織への招待',
  },
  teamColInvitee: { en: 'Invitee', ja: '招待先' },
  teamColExpires: { en: 'Expires', ja: '有効期限' },
  teamLoadingInvitations: { en: 'Loading invitations', ja: '招待を読み込んでいます' },
  teamNoPendingInvitations: { en: 'No pending invitations.', ja: '保留中の招待はありません。' },
  teamResend: { en: 'Resend', ja: '再送信' },
  teamMoreActionsForInvitation: {
    en: 'More actions for invitation {email}',
    ja: '招待{email}のその他の操作',
  },
  teamRevoke: { en: 'Revoke', ja: '取り消し' },
  onboardingConsoleSessionUnavailable: {
    en: 'Console session is unavailable',
    ja: 'コンソールのセッションを利用できません',
  },
  onboardingOrgNameRequired: { en: 'Organization name is required.', ja: '組織名は必須です。' },
  onboardingOrgNameTooShort: {
    en: 'Organization name must be at least 2 characters.',
    ja: '組織名は2文字以上にしてください。',
  },
  onboardingOrgNameTooLong: {
    en: 'Organization name must be 80 characters or fewer.',
    ja: '組織名は80文字以内にしてください。',
  },
  onboardingProjectNameRequired: {
    en: 'Project name is required.',
    ja: 'プロジェクト名は必須です。',
  },
  onboardingProjectNameTooLong: {
    en: 'Project name must be 80 characters or fewer.',
    ja: 'プロジェクト名は80文字以内にしてください。',
  },
  onboardingPermissionMissing: {
    en: 'Permission missing. Ask an organization owner/admin for onboarding access and retry.',
    ja: '権限がありません。組織のオーナーまたは管理者にオンボーディングへのアクセスを依頼して、もう一度お試しください。',
  },
  onboardingInvalidInput: {
    en: 'Invalid input format. Update the form values and retry.',
    ja: '入力形式が正しくありません。フォームの値を修正して、もう一度お試しください。',
  },
  onboardingProjectNameUsed: {
    en: 'Name already used. Choose a different project name and retry.',
    ja: 'この名前はすでに使われています。別のプロジェクト名を選んで、もう一度お試しください。',
  },
  onboardingNameOrgFirst: {
    en: 'Name your organization before creating your first project.',
    ja: '最初のプロジェクトを作成する前に、組織に名前を付けてください。',
  },
  onboardingNameArchived: {
    en: 'Name already used. That name belongs to an archived resource, so choose a new name.',
    ja: 'この名前はすでに使われています。この名前はアーカイブ済みのリソースのものなので、新しい名前を選んでください。',
  },
  onboardingRequestFailed: {
    en: 'Request failed. Update the inputs and retry.',
    ja: 'リクエストに失敗しました。入力内容を修正して、もう一度お試しください。',
  },
  onboardingNameUsed: {
    en: 'Name already used. Choose a different value and retry.',
    ja: 'この名前はすでに使われています。別の値を選んで、もう一度お試しください。',
  },
  onboardingStepCurrent: { en: 'Current', ja: '現在' },
  onboardingStepDone: { en: 'Done', ja: '完了' },
  onboardingStepLocked: { en: 'Locked', ja: 'ロック中' },
  onboardingCompleteOrgFirst: {
    en: 'Complete organization setup before creating your first project.',
    ja: '最初のプロジェクトを作成する前に、組織の設定を完了してください。',
  },
  onboardingStepOrganization: { en: 'Organization', ja: '組織' },
  onboardingStepProject: { en: 'Project', ja: 'プロジェクト' },
  onboardingUnavailable: {
    en: 'Onboarding unavailable: {reason}.',
    ja: 'オンボーディングを利用できません: {reason}',
  },
  onboardingLoadingStatus: {
    en: 'Loading onboarding status...',
    ja: 'オンボーディングの状況を読み込んでいます…',
  },
  onboardingStatusUnavailable: {
    en: 'Onboarding status unavailable: {message}',
    ja: 'オンボーディングの状況を利用できません: {message}',
  },
  onboardingUnauthorized: { en: 'unauthorized', ja: '権限がありません' },
  onboardingCreateOrganizationAria: { en: 'Create organization', ja: '組織の作成' },
  onboardingNameYourOrganization: { en: 'Name your organization', ja: '組織に名前を付ける' },
  onboardingNameVisibleHint: {
    en: 'Your team sees this name in the Console. You can change it later.',
    ja: 'この名前はコンソールでチームに表示されます。あとから変更できます。',
  },
  onboardingOrganizationName: { en: 'Organization name', ja: '組織名' },
  onboardingHandle: { en: 'Handle:', ja: 'ハンドル:' },
  onboardingSavingOrganization: { en: 'Saving organization...', ja: '組織を保存しています…' },
  onboardingContinue: { en: 'Continue', ja: '続行' },
  onboardingSavedAs: { en: 'Saved as {name}.', ja: '{name}として保存しました。' },
  onboardingCreateProjectAria: { en: 'Create project', ja: 'プロジェクトの作成' },
  onboardingCreateFirstProject: { en: 'Create your first project', ja: '最初のプロジェクトを作成' },
  onboardingProjectHelp: {
    en: 'A project holds your app’s wallets, keys and policies. Wallet sets itself up when you open the project.',
    ja: 'プロジェクトには、アプリのウォレット、鍵、ポリシーが含まれます。プロジェクトを開くと、Walletが自動的にセットアップされます。',
  },
  onboardingProjectName: { en: 'Project name', ja: 'プロジェクト名' },
  onboardingRenameLater: { en: 'You can rename it later.', ja: 'あとから名前を変更できます。' },
  onboardingBack: { en: 'Back', ja: '戻る' },
  onboardingCreatingProject: { en: 'Creating project...', ja: 'プロジェクトを作成しています…' },
  onboardingCreateProject: { en: 'Create project', ja: 'プロジェクトを作成' },
  onboardingOpeningOverview: { en: 'Opening overview...', ja: '概要を開いています…' },
  onboardingWizardPageAria: {
    en: 'Onboarding wizard page',
    ja: 'オンボーディングウィザードページ',
  },
  onboardingSetUpWorkspace: { en: 'Set up your workspace', ja: 'ワークスペースをセットアップ' },
  onboardingProgressAria: { en: 'Onboarding progress', ja: 'オンボーディングの進捗' },
  onboardingRetrying: { en: 'Retrying...', ja: '再試行しています…' },
  onboardingRetry: { en: 'Retry', ja: '再試行' },
} as const satisfies Record<string, Entry>;
